import { create } from 'zustand'
import { NotificationError } from '@/components/ErrorNotification'
import { logger } from '@/utils/logger'

interface ErrorNotificationStore {
    errors: NotificationError[]
    addError: (error: Omit<NotificationError, 'id'>) => void
    removeError: (id: string) => void
    clearAllErrors: () => void
    
    // Convenience methods for common error types
    addNetworkError: (message: string, onRetry?: () => void) => void
    addApiError: (title: string, message: string, isRetryable?: boolean, onRetry?: () => void) => void
    addValidationError: (message: string) => void
    addSuccessNotification: (title: string, message: string) => void
}

const useErrorNotificationStore = create<ErrorNotificationStore>((set, get) => ({
    errors: [],

    addError: (errorData) => {
        const error: NotificationError = {
            id: `error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            ...errorData
        }

        logger.error('component', 'Error notification added', {
            errorId: error.id,
            type: error.type,
            title: error.title
        })

        set(state => ({
            errors: [...state.errors, error]
        }))
    },

    removeError: (id) => {
        logger.debug('component', `Error notification removed: ${id}`)
        
        set(state => ({
            errors: state.errors.filter(error => error.id !== id)
        }))
    },

    clearAllErrors: () => {
        const errorCount = get().errors.length
        if (errorCount > 0) {
            logger.info('component', `Cleared ${errorCount} error notifications`)
        }
        
        set({ errors: [] })
    },

    // Convenience methods
    addNetworkError: (message, onRetry) => {
        get().addError({
            title: 'Connection Error',
            message,
            type: 'network',
            isRetryable: true,
            onRetry,
            autoHide: false, // Don't auto-hide network errors
            duration: 0
        })
    },

    addApiError: (title, message, isRetryable = false, onRetry) => {
        get().addError({
            title,
            message,
            type: 'error',
            isRetryable,
            onRetry,
            autoHide: true,
            duration: 5000
        })
    },

    addValidationError: (message) => {
        get().addError({
            title: 'Validation Error',
            message,
            type: 'warning',
            isRetryable: false,
            autoHide: true,
            duration: 4000
        })
    },

    addSuccessNotification: (title, message) => {
        get().addError({
            title,
            message,
            type: 'success',
            isRetryable: false,
            autoHide: true,
            duration: 3000
        })
    }
}))

export default useErrorNotificationStore